import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, MessageSquare, MessageSquareOff, Loader, Download, Settings } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VideoPlayerPage = ({ user }) => {
  const { videoId } = useParams();
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [captionsEnabled, setCaptionsEnabled] = useState(true);
  const [selectedLanguage, setSelectedLanguage] = useState('original');
  const [captionUrl, setCaptionUrl] = useState(null);
  const [showCaptionMenu, setShowCaptionMenu] = useState(false);
  const videoRef = useRef(null);
  const [videoUrl, setVideoUrl] = useState(null);

  useEffect(() => {
    fetchVideo();
  }, [videoId]);

  useEffect(() => {
    if (video && selectedLanguage) {
      fetchCaptions(selectedLanguage);
    }
  }, [video, selectedLanguage]);

  const fetchCaptions = async (lang) => {
    try {
      const token = localStorage.getItem('kaplie_token');
      const response = await axios.get(
        `${API}/videos/${videoId}/captions?lang=${lang}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      const url = URL.createObjectURL(response.data);
      setCaptionUrl(url);
    } catch (error) {
      console.error('Failed to load captions:', error);
    }
  };

  const handleDownloadCaptions = async (lang, format = 'vtt') => {
    try {
      const token = localStorage.getItem('kaplie_token');
      const response = await axios.get(
        `${API}/videos/${videoId}/captions/download?lang=${lang}&format=${format}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      
      const langNames = { original: video.detected_language || 'original', en: 'english', es: 'spanish' };
      const fileName = `${video.filename.split('.')[0]}_${langNames[lang]}.${format}`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Captions downloaded (${format.toUpperCase()})`);
    } catch (error) {
      toast.error('Failed to download captions');
    }
  };

  const fetchVideo = async () => {
    try {
      const token = localStorage.getItem('kaplie_token');
      const response = await axios.get(`${API}/videos/${videoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVideo(response.data);

      // Fetch video blob
      const videoResponse = await axios.get(`${API}/videos/${videoId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = URL.createObjectURL(videoResponse.data);
      setVideoUrl(url);
    } catch (error) {
      toast.error('Failed to load video');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (videoUrl) {
        URL.revokeObjectURL(videoUrl);
      }
      if (captionUrl) {
        URL.revokeObjectURL(captionUrl);
      }
    };
  }, [videoUrl, captionUrl]);

  useEffect(() => {
    if (videoRef.current && video?.captions && captionsEnabled) {
      const track = videoRef.current.querySelector('track');
      if (track) {
        track.track.mode = 'showing';
      }
    }
  }, [video, captionsEnabled, videoUrl]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-black/40">
        <Loader className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (!video) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-black/40 text-white">
        <h2 className="text-2xl font-bold mb-4">Video not found</h2>
        <Link to="/my-videos">
          <button className="px-6 py-3 font-bold bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-black rounded-full hover:shadow-[0_0_30px_rgba(212,175,55,0.35)] transition-all">
            Back to My Videos
          </button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black/40 text-white">
      {/* Navigation */}
      <nav className="border-b border-white/[0.08] bg-white/[0.03]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="text-2xl font-bold tracking-tighter">Kaplie</div>
          <Link to="/my-videos" data-testid="back-to-videos-link">
            <button className="flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              Back to My Videos
            </button>
          </Link>
        </div>
      </nav>

      {/* Video Player */}
      <div className="max-w-6xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold tracking-tighter mb-6" data-testid="video-title">
          {video.filename}
        </h1>

        {/* Video Container */}
        <div className="relative bg-black rounded-2xl overflow-hidden shadow-2xl" data-testid="video-player">
          {videoUrl && (
            <video
              ref={videoRef}
              controls
              className="w-full aspect-video"
              crossOrigin="anonymous"
              key={selectedLanguage}
            >
              <source src={videoUrl} type={video.content_type} />
              {captionUrl && (
                <track
                  label="Captions"
                  kind="subtitles"
                  srcLang={selectedLanguage}
                  src={captionUrl}
                  default={captionsEnabled}
                />
              )}
              Your browser does not support the video tag.
            </video>
          )}

          {/* Language & Caption Controls */}
          <div className="absolute bottom-20 right-6 z-10 flex gap-3">
            {/* Caption Settings Menu */}
            {video?.captions_original && (
              <div className="relative">
                <button
                  onClick={() => setShowCaptionMenu(!showCaptionMenu)}
                  data-testid="caption-settings-button"
                  className="p-3 rounded-full bg-white/[0.03] border border-white/[0.08] text-white hover:bg-[#1E1E24] transition-all shadow-lg"
                >
                  <Settings className="w-5 h-5" />
                </button>

                {/* Caption Menu Dropdown */}
                {showCaptionMenu && (
                  <div className="absolute bottom-full right-0 mb-2 w-72 bg-white/[0.03] border border-white/[0.08] rounded-2xl shadow-2xl overflow-hidden" data-testid="caption-menu">
                    <div className="p-4 border-b border-white/[0.08]">
                      <h3 className="font-bold text-sm mb-1">Caption Settings</h3>
                      <p className="text-xs text-[#A1A1AA]">Language & Download Options</p>
                    </div>

                    {/* Language Selection */}
                    <div className="p-4 border-b border-white/[0.08]">
                      <label className="block text-sm font-medium mb-2">Caption Language</label>
                      <select
                        value={selectedLanguage}
                        onChange={(e) => {
                          setSelectedLanguage(e.target.value);
                          setShowCaptionMenu(false);
                        }}
                        data-testid="language-selector-dropdown"
                        className="w-full px-3 py-2 bg-black/40 border border-white/[0.08] text-white rounded-lg focus:border-[#D4AF37]/50 focus:outline-none"
                      >
                        <option value="original">
                          Original {video.detected_language ? `(${video.detected_language.toUpperCase()})` : ''}
                        </option>
                        <option value="en">English</option>
                        <option value="es">Español</option>
                      </select>
                    </div>

                    {/* Download Options */}
                    <div className="p-4">
                      <label className="block text-sm font-medium mb-3">Download Captions</label>
                      <div className="space-y-2">
                        <button
                          onClick={() => {
                            handleDownloadCaptions(selectedLanguage, 'vtt');
                            setShowCaptionMenu(false);
                          }}
                          data-testid="download-vtt-button"
                          className="w-full flex items-center justify-between px-3 py-2 bg-black/40 hover:bg-[#1E1E24] border border-white/[0.08] rounded-lg transition-colors text-sm"
                        >
                          <span>WebVTT (.vtt)</span>
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            handleDownloadCaptions(selectedLanguage, 'srt');
                            setShowCaptionMenu(false);
                          }}
                          data-testid="download-srt-button"
                          className="w-full flex items-center justify-between px-3 py-2 bg-black/40 hover:bg-[#1E1E24] border border-white/[0.08] rounded-lg transition-colors text-sm"
                        >
                          <span>SubRip (.srt)</span>
                          <Download className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Download All Languages */}
                      <div className="mt-3 pt-3 border-t border-white/[0.08]">
                        <p className="text-xs text-[#A1A1AA] mb-2">Quick download all:</p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              handleDownloadCaptions('original', 'vtt');
                              handleDownloadCaptions('en', 'vtt');
                              handleDownloadCaptions('es', 'vtt');
                              setShowCaptionMenu(false);
                            }}
                            className="flex-1 px-2 py-1.5 text-xs bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37]/20 rounded-lg transition-colors"
                          >
                            All (.vtt)
                          </button>
                          <button
                            onClick={() => {
                              handleDownloadCaptions('original', 'srt');
                              handleDownloadCaptions('en', 'srt');
                              handleDownloadCaptions('es', 'srt');
                              setShowCaptionMenu(false);
                            }}
                            className="flex-1 px-2 py-1.5 text-xs bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37]/20 rounded-lg transition-colors"
                          >
                            All (.srt)
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Caption Toggle Button */}
            <button
              onClick={() => {
                setCaptionsEnabled(!captionsEnabled);
                if (videoRef.current) {
                  const track = videoRef.current.querySelector('track');
                  if (track) {
                    track.track.mode = !captionsEnabled ? 'showing' : 'hidden';
                  }
                }
              }}
              data-testid="toggle-captions-button"
              className={`p-3 rounded-full transition-all shadow-lg ${
                captionsEnabled
                  ? 'bg-gradient-to-r from-[#D4AF37] to-[#C5A059] hover:shadow-[0_0_20px_rgba(212,175,55,0.3)]'
                  : 'bg-white/[0.03] hover:bg-[#1E1E24] border border-white/[0.08]'
              }`}
            >
              {captionsEnabled ? (
                <MessageSquare className="w-5 h-5 text-black" />
              ) : (
                <MessageSquareOff className="w-5 h-5 text-white" />
              )}
            </button>
          </div>
        </div>

        {/* Video Info */}
        <div className="mt-8 bg-white/[0.03] border border-white/[0.08] rounded-2xl p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <p className="text-[#A1A1AA] text-sm mb-1">Duration</p>
              <p className="font-bold">{video.duration ? `${Math.floor(video.duration / 60)}:${Math.floor(video.duration % 60).toString().padStart(2, '0')}` : 'N/A'}</p>
            </div>
            <div>
              <p className="text-[#A1A1AA] text-sm mb-1">Uploaded</p>
              <p className="font-bold">{new Date(video.created_at).toLocaleDateString()}</p>
            </div>
            <div>
              <p className="text-[#A1A1AA] text-sm mb-1">Detected Language</p>
              <p className="font-bold">{video.detected_language ? video.detected_language.toUpperCase() : 'Unknown'}</p>
            </div>
            <div>
              <p className="text-[#A1A1AA] text-sm mb-1">Captions</p>
              <p className="font-bold">{video.captions_original ? 'Available' : 'Processing...'}</p>
            </div>
          </div>

          {video.captions_original && (
            <div className="mt-6 pt-6 border-t border-white/[0.08]">
              <div className="flex items-start gap-3">
                <MessageSquare className="w-5 h-5 text-[#22C55E] flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-[#22C55E] mb-1">Multi-language captions available</p>
                  <p className="text-[#A1A1AA]">
                    Watch with captions in Original, English, or Spanish. Select your preferred language from the dropdown above the video.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Custom Styles for Video Captions */}
      <style jsx>{`
        video::cue {
          font-family: 'JetBrains Mono', monospace;
          font-size: 1.5rem;
          font-weight: 700;
          color: #D4AF37;
          background: rgba(0, 0, 0, 0.8);
          text-shadow: 0 4px 12px rgba(0, 0, 0, 0.8), 0 2px 4px rgba(0, 0, 0, 0.6);
          padding: 0.5rem 1rem;
          border-radius: 0.5rem;
        }
      `}</style>
    </div>
  );
};

export default VideoPlayerPage;
